import { Headline } from '@oneaudi/unified-web-alpha-components';
import { useAtom } from 'jotai';
import { useCallback, useMemo } from 'react';
import { useI18n } from '~/app/i18n/i18n';
import { useMarketConfig } from '~/app/market-config';
import { useVATConfig } from '~/app/market-config/use-vat-config';
import { useMoreInformationDialog } from '~/app/pages/configurator/components/more-information-dialog';
import { appTrackingService } from '~/app/tracking/app-tracking-service';
import { EventAction } from '~/app/tracking/feld-m-types';
import { useDebugMode } from '~/app/utils/debug-mode';
import { SeatCoversFallbackSvg } from '~/components/image-selection-tile/components/fallback-svgs/seat-cover-fallback-svg';
import { SelectionContainer } from '~/components/image-selection-tile/style/tile-selection-section-style';
import { TilesSelection } from '~/components/image-selection-tile/tiles-selection';
import { useCatalog, useConfigurator } from '~/graphql/hooks/use-configurator';
import { FeatureCategory, FeatureOrPackage, SelectableOption } from '~/graphql/types';
import { state } from '~/storage';
import { createRecord } from '~/utils/create-record';

export function SeatCovers({ step, total }: { step: number; total: number }) {
	const { i18n } = useI18n();
	const { loadingFlags, isItemChecked, isItemLoading, changeConfiguration } = useConfigurator();
	const { openMoreInformationDialog } = useMoreInformationDialog();
	const { interiorTiles } = useCatalog();
	const [activeFilters] = useAtom(state.activeFilters);
	const { config } = useMarketConfig();
	const { getName } = useDebugMode();
	const { feature } = useVATConfig();

	const trackSelectionClickEvent = useCallback(
		(item: FeatureOrPackage, isSelected: boolean) => {
			const seatCoversFilters = activeFilters?.seatCovers?.length > 0 ? activeFilters?.seatCovers : 'all';

			appTrackingService.trackSelectionClickEvent({
				eventAction: EventAction.InternalLink,
				eventName: 'click to change configuration',
				label: item.name,
				elementName: 'image',
				value: `selection feature app: seatCovers: ${seatCoversFilters}`,
				category: 'seatCovers',
				elementCurrentStep: step,
				elementTotalSteps: total,
				selected: isSelected,
				item,
			});
		},
		[activeFilters?.seats, step, total],
	);

	const { items, filters } = useMemo(() => {
		if (!interiorTiles) return { items: [], filters: [] };

		// Create filters according to subFamilies
		const subFamilies = createRecord(interiorTiles, (tile) => tile.group.name);

		// Map items for the tile selection
		const mappedItems = interiorTiles.map((tile) => ({
			name: getName(tile) ?? ' ',
			key: tile.featureId,
			onSelect: () => {
				trackSelectionClickEvent(tile.__item, isItemChecked(tile));
				changeConfiguration(tile as SelectableOption<FeatureCategory>);
			},
			loading: isItemLoading(tile),
			readonly: !tile.status.changeable,
			familyName: tile.group.name ?? '',
			subFamilyName: tile.family.name,
			teaserText: tile.__item.texts?.intro || '',
			standard: tile.status.standard,
			imgUrl: tile.interiorTileButtonUrl || '',
			selected: isItemChecked(tile),
			price: config.showPrices ? { totalPrice: tile.totalPrice, feature } : undefined,
			textLink: {
				label: i18n('ngc.actions.moreInfo'),
				onTextLinkClick: () =>
					openMoreInformationDialog(
						tile as SelectableOption<FeatureCategory>,
						tile.__item,
						() => {},
						step,
						total,
						'seatCover',
					),
			},
			disclaimerHeadline: tile.__item.texts?.disclaimerHeadline || undefined,
			disclaimerContent: tile.__item.texts?.disclaimerText || undefined,
			disclaimerStartDate: tile.__item.texts?.disclaimerStartDate || undefined,
			disclaimerEndDate: tile.__item.texts?.disclaimerEndDate || undefined,
		}));

		return {
			filters: Object.keys(subFamilies),
			items: mappedItems,
		};
	}, [
		changeConfiguration,
		config.showPrices,
		getName,
		interiorTiles,
		isItemChecked,
		isItemLoading,
		feature,
		trackSelectionClickEvent,
	]);

	return (
		<>
			<SelectionContainer items={interiorTiles?.length} tabIndex={0}>
				<Headline className="subtitle" tag="h2" variant={{ order: '4', type: 'headline', weight: 'normal' }}>
					{i18n('ngc.equipment.seatCover')}
				</Headline>
				<TilesSelection
					id="seatCovers"
					items={items}
					filters={filters}
					fallbackSVG={SeatCoversFallbackSvg()}
					isLoading={loadingFlags.checkingConflicts}
					step={step}
					totalSteps={total}
				/>
			</SelectionContainer>
		</>
	);
}
